import React, { useMemo, useState } from 'react';
import { SBA } from '../../theme';
import {
  GED_ER_STRUCTURE,
  GED_ER_TRAITS,
  ER_WORD_TARGET_MAX,
  ER_WORD_TARGET_MIN,
  countWords,
} from '../../utils/extendedResponses';
import { MAX_QUIZ_TRIES_BEFORE_SKIP } from '../../utils/studentQuizzes';

export default function ExtendedResponseWriter({
  item,
  subjectColor = SBA.navy,
  attemptsUsed = 0,
  result = null,
  practiceOnly = false,
  submitting = false,
  onSubmit,
  onRetry,
  onContinueAnyway,
  onCloseResults,
}) {
  const [draft, setDraft] = useState('');
  const words = countWords(draft);
  const inBand = words >= ER_WORD_TARGET_MIN && words <= ER_WORD_TARGET_MAX;
  const canSkip = !practiceOnly && attemptsUsed >= MAX_QUIZ_TRIES_BEFORE_SKIP;
  const passed = Boolean(result?.passed);

  const wordHint = useMemo(() => {
    if (words < ER_WORD_TARGET_MIN) {
      return `Aim for about ${ER_WORD_TARGET_MIN}–${ER_WORD_TARGET_MAX} words. Quality of argument matters more than hitting a number.`;
    }
    if (words > ER_WORD_TARGET_MAX) {
      return 'You are past the usual 300–500 word range. Extra length is fine if every paragraph still develops the argument.';
    }
    return 'You are in the usual GED ER length range. Keep focusing on evidence and organization.';
  }, [words]);

  async function handleSubmit() {
    if (!draft.trim() || submitting) return;
    await onSubmit(draft);
  }

  if (result) {
    return (
      <div style={st.wrap}>
        <div style={st.scoreHead(passed)}>
          <span className="material-symbols-outlined" style={{ fontSize: 28 }}>
            {passed ? 'verified' : 'info'}
          </span>
          <div>
            <h2 style={st.h2}>{passed ? 'Extended Response passed' : 'Not quite yet'}</h2>
            <p style={st.sub}>
              GED traits: {result.total}/6
              {typeof result.displayPct === 'number' ? ` (${result.displayPct}%)` : ''}
              {result.wordCount ? ` · ${result.wordCount} words` : ''}
            </p>
            <p style={st.sub}>Pass needs 4/6 and at least 1 on Trait 1 (argument from the passages).</p>
          </div>
        </div>
        <div style={st.traitGrid}>
          {[
            { n: 1, title: 'Argument & Evidence', fb: result.feedbackTrait1, score: result.trait1 },
            { n: 2, title: 'Development & Organization', fb: result.feedbackTrait2, score: result.trait2 },
            { n: 3, title: 'English Conventions', fb: result.feedbackTrait3, score: result.trait3 },
          ].map((t) => (
            <div key={t.n} style={st.traitCard}>
              <div style={st.traitTop}>
                <strong>Trait {t.n}</strong>
                <span>{t.score}/2</span>
              </div>
              <p style={st.traitName}>{t.title}</p>
              <p style={st.fb}>{t.fb || 'No comment.'}</p>
            </div>
          ))}
        </div>
        <div style={st.actions}>
          {(practiceOnly || !passed) ? (
            <button
              type="button"
              onClick={() => {
                onRetry?.();
              }}
              style={st.primary(subjectColor)}
            >
              Retry Extended Response
            </button>
          ) : null}
          {!practiceOnly && passed ? (
            <button type="button" onClick={onCloseResults} style={st.primary(subjectColor)}>
              Continue
            </button>
          ) : null}
          {!practiceOnly && !passed && canSkip ? (
            <button type="button" onClick={onContinueAnyway} style={st.outline(subjectColor)}>
              Move on
            </button>
          ) : null}
          {!practiceOnly && !passed && !canSkip ? (
            <p style={st.sub}>If you miss again, you can retry or move on.</p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div style={st.wrap}>
      <p style={st.kicker}>Language Arts · GED Extended Response</p>
      <h1 style={st.h1}>{item.title}</h1>
      <p style={st.lead}>
        Read the prompt and passages. Then write which position is better supported by the evidence — even if you personally disagree.
      </p>

      <section style={st.block}>
        <h2 style={st.h2}>Prompt and passages</h2>
        <div style={st.prompt}>{item.prompt}</div>
      </section>

      <section style={st.block}>
        <h2 style={st.h2}>How this is scored</h2>
        <p style={st.sub}>Official GED RLA traits (0–2 each). Paragraph count does not earn a high score.</p>
        <ul style={st.list}>
          {GED_ER_TRAITS.map((t) => (
            <li key={t.id}>
              <strong>{t.title}.</strong> {t.question}
            </li>
          ))}
        </ul>
      </section>

      <section style={st.block}>
        <h2 style={st.h2}>Recommended structure</h2>
        <p style={st.sub}>A fully developed response is often 4–7 paragraphs. Use this outline as a guide, not a checklist for points.</p>
        <ol style={st.list}>
          {GED_ER_STRUCTURE.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ol>
      </section>

      <label style={st.label} htmlFor="er-draft">
        Your extended response
      </label>
      <textarea
        id="er-draft"
        style={st.textarea}
        rows={16}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Write your claim, evidence from the passages, analysis, the weaker opposing side, and a conclusion."
        disabled={submitting}
      />
      <div style={st.wordRow}>
        <span style={{ fontWeight: 700, color: inBand ? '#15803d' : '#b45309' }}>{words} words</span>
        <span style={st.sub}>{wordHint}</span>
      </div>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={submitting || countWords(draft) < 40}
        style={{
          ...st.primary(subjectColor),
          opacity: submitting || countWords(draft) < 40 ? 0.55 : 1,
        }}
      >
        {submitting ? 'Scoring…' : 'Submit for GED scoring'}
      </button>
      {countWords(draft) < 40 ? (
        <p style={st.sub}>Write a bit more before submitting (at least a short introduction and one evidence paragraph).</p>
      ) : null}
    </div>
  );
}

const st = {
  wrap: { maxWidth: 760, margin: '0 auto', padding: '8px 4px 40px' },
  kicker: { fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b', margin: '0 0 6px' },
  h1: { fontSize: 26, fontWeight: 800, color: '#0f172a', margin: '0 0 10px', lineHeight: 1.25 },
  h2: { fontSize: 16, fontWeight: 800, color: '#1e293b', margin: '0 0 8px' },
  lead: { fontSize: 15, color: '#334155', lineHeight: 1.6, margin: '0 0 18px' },
  sub: { fontSize: 13, color: '#64748b', lineHeight: 1.5, margin: '4px 0 0' },
  block: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 14,
    padding: '16px 18px',
    marginBottom: 14,
  },
  prompt: {
    whiteSpace: 'pre-wrap',
    fontSize: 14.5,
    lineHeight: 1.65,
    color: '#1e293b',
  },
  list: { margin: '8px 0 0', paddingLeft: 18, color: '#334155', fontSize: 14, lineHeight: 1.55 },
  label: { display: 'block', fontSize: 13, fontWeight: 700, color: '#0f172a', margin: '10px 0 8px' },
  textarea: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1.5px solid #cbd5e1',
    borderRadius: 12,
    padding: 14,
    fontSize: 15,
    lineHeight: 1.6,
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    resize: 'vertical',
    minHeight: 280,
  },
  wordRow: { display: 'flex', flexDirection: 'column', gap: 4, margin: '10px 0 16px' },
  primary: (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    background: color,
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    padding: '12px 18px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  }),
  outline: (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    color,
    border: `1.5px solid ${color}`,
    borderRadius: 12,
    padding: '12px 18px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  }),
  actions: { display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 },
  scoreHead: (passed) => ({
    display: 'flex',
    gap: 14,
    alignItems: 'flex-start',
    padding: 16,
    borderRadius: 14,
    marginBottom: 14,
    background: passed ? '#ecfdf5' : '#fff7ed',
    color: passed ? '#047857' : '#9a3412',
  }),
  traitGrid: { display: 'grid', gap: 10, marginBottom: 16 },
  traitCard: {
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: 14,
    background: '#fff',
  },
  traitTop: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#0f172a' },
  traitName: { fontSize: 14, fontWeight: 700, margin: '4px 0 6px', color: '#1e293b' },
  fb: { fontSize: 13.5, color: '#475569', lineHeight: 1.5, margin: 0 },
};
